type Price = `${number}` | number;
export type UseMoneyValue = {
  currencySymbol: () => string;
  amount: () => string;
  parts: () => Intl.NumberFormatPart[];
  withoutTrailingZeros: () => string;
};

const formatMoney = (
  locale: string,
  options?: Intl.NumberFormatOptions
): Intl.NumberFormat => {
  return new Intl.NumberFormat(locale, options);
};

export const useMoney = (price: Price): UseMoneyValue => {
  if (!price) return null;

  const locale =
    `${window.Shopify.locale}-${window.Shopify.country}` || "en-US";
  const amount = parseFloat(price.toString());
  const currency = window.Shopify.currency.active || "USD";
  const options = {
    style: "currency",
    currency,
  };

  const isPartCurrency = (part: Intl.NumberFormatPart): boolean =>
    part.type === "currency";

  return {
    parts: () => formatMoney(locale, options).formatToParts(amount),
    currencySymbol: () =>
      formatMoney(locale, options).formatToParts(amount).find(isPartCurrency)
        ?.value ?? currency,
    withoutTrailingZeros: () =>
      amount % 1 === 0
        ? formatMoney(locale, {
            ...options,
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
          }).format(amount)
        : formatMoney(locale, options).format(amount),
    amount: () =>
      formatMoney(locale, options)
        .formatToParts(amount)
        .filter((part) =>
          ["decimal", "fraction", "group", "integer", "literal"].includes(
            part.type
          )
        )
        .map((part) => part.value)
        .join(""),
  };
};

export const useVariantPrice = (
  variant
): { price: UseMoneyValue; compareAtPrice: UseMoneyValue } => {
  // > [!NOTE] Replace and validate using storefront query to get global discounts
  if (!window.globalDiscounts) {
    return {
      price: useMoney(variant.price),
      compareAtPrice: useMoney(variant.compare_at_price),
    };
  }

  const oneTimeDiscount = window.globalDiscounts.find(
    (globalDiscount) => globalDiscount.purchase_type === "One-time purchase"
  );
  if (!oneTimeDiscount)
    return {
      price: useMoney(variant.price),
      compareAtPrice: useMoney(variant.compare_at_price),
    };

  const amount = parseFloat(variant.price.toString());
  const discountValue = parseFloat(oneTimeDiscount.discount_value.toString());
  if (oneTimeDiscount.discount_value_type === "Fixed Amount") {
    return {
      price: useMoney(`${amount - discountValue / 100}`),
      compareAtPrice:
        useMoney(variant.compare_at_price) || useMoney(variant.price),
    };
  } else if (oneTimeDiscount.discount_value_type === "Percentage") {
    const discountPercentage = discountValue / 100;
    return {
      price: useMoney(
        `${Math.ceil((amount - amount * discountPercentage) * 100) / 100}`
      ),
      compareAtPrice:
        useMoney(variant.compare_at_price) || useMoney(variant.price),
    };
  }
};
